// Strava-style polyline-canvas heatmap with a Float32 accumulator.
//
// We don't draw to a canvas with low-alpha strokes because Canvas2D
// works in 8-bit per channel — sub-pixel antialiasing, lineCap/lineJoin
// rounding, and the 0–255 ceiling make "1 traversal" wildly different
// from "1 unit of intensity". A line at lineWidth=0.5 with a high
// vertex count near curves accumulates several passes' worth of alpha
// on the same pixels from a single polyline.
//
// Instead we maintain a Float32Array of size W·H and rasterize each
// polyline segment with integer Bresenham, adding exactly +1 to every
// pixel the segment crosses. Endpoint pixels are skipped between
// adjoining segments so vertices aren't double-counted. The result is
// a clean count grid: pixel value = number of polylines crossing this
// pixel. We then apply gamma compression and the orange→cream LUT in
// one ImageData pass.
//
// Why this matches Strava's heatmap quality at every zoom:
//   - Lines are exactly 1 px wide on screen, never blurry on zoom-out.
//   - Heat is exactly polyline-overlap count — no antialiasing or
//     vertex-rounding noise.
//   - No 8-bit clamping: dynamic range is bounded only by Float32.
//   - Redraw on moveend/zoomend/resize → snap-sharp after every gesture.

import { useEffect, useRef } from 'react';
import { useMap } from 'react-leaflet';
import L from 'leaflet';


// Curated heatmap palettes — hand-tuned gradient stops per color, each
// with a clear progression of distinguishable colors across the range
// (low/mid/high pass counts read visibly different from each other).
//
// Format per stop: [R, G, B, A] in 0..255. Positions are fixed in
// NODE_POSITIONS so the LUT builder doesn't need per-palette plumbing.
//
// Mirrors HEATMAP_PALETTES in backend/heatmap.py.
const NODE_POSITIONS = [0.0, 0.01, 0.2, 0.5, 0.8, 1.0];

const HEATMAP_PALETTES = {
  // Strava orange — classic frequency-heatmap look.
  orange: [
    [0, 0, 0, 0],
    [102, 26, 0, 140],
    [252, 76, 2, 204],
    [255, 166, 0, 235],
    [255, 235, 51, 247],
    [255, 255, 204, 255],
  ],
  // Fire — dark red → red → orange → yellow → cream.
  red: [
    [0, 0, 0, 0],
    [60, 0, 0, 140],
    [200, 20, 10, 204],
    [240, 70, 10, 235],
    [255, 200, 40, 247],
    [255, 250, 200, 255],
  ],
  // Cool — dark navy → royal blue → sky → cyan → near-white.
  blue: [
    [0, 0, 0, 0],
    [0, 20, 80, 140],
    [10, 70, 200, 204],
    [40, 170, 235, 235],
    [150, 230, 250, 247],
    [225, 248, 255, 255],
  ],
  // Forest — dark green → green → lime → yellow-green → cream.
  green: [
    [0, 0, 0, 0],
    [0, 45, 10, 140],
    [20, 140, 30, 204],
    [120, 210, 40, 235],
    [220, 250, 110, 247],
    [248, 255, 220, 255],
  ],
  // Galaxy — dark purple → violet → magenta → pink → cream.
  purple: [
    [0, 0, 0, 0],
    [40, 0, 60, 140],
    [115, 30, 180, 204],
    [225, 70, 205, 235],
    [255, 185, 225, 247],
    [255, 232, 248, 255],
  ],
};

const DEFAULT_PALETTE_KEY = 'orange';

// Resolve a `baseColor` prop (legacy: any CSS color name) to one of
// the curated palette keys. We do a name match on known keys; anything
// unrecognized falls back to orange so old saved settings stay safe.
function resolvePaletteNodes(name) {
  if (!name) return HEATMAP_PALETTES[DEFAULT_PALETTE_KEY];
  const key = String(name).toLowerCase();
  return HEATMAP_PALETTES[key] || HEATMAP_PALETTES[DEFAULT_PALETTE_KEY];
}

const LUT_SIZE = 256;
const _lutCache = new Map();

function buildLut(gamma, baseColor) {
  const lut = new Uint8ClampedArray(LUT_SIZE * 4);
  const nodes = resolvePaletteNodes(baseColor);
  for (let i = 0; i < LUT_SIZE; i++) {
    // Gamma compression: pushes low intensities UP the gradient so
    // rarely-traversed routes show in a distinguishable color instead
    // of compressing into a thin sliver at the bottom. gamma=1.0
    // disables this (linear).
    const raw = i / (LUT_SIZE - 1);
    const t = gamma === 1 ? raw : Math.pow(raw, gamma);

    let lo = 0;
    for (let j = 0; j < NODE_POSITIONS.length - 1; j++) {
      if (t >= NODE_POSITIONS[j] && t <= NODE_POSITIONS[j + 1]) {
        lo = j;
        break;
      }
    }
    const t0 = NODE_POSITIONS[lo];
    const t1 = NODE_POSITIONS[lo + 1];
    const c0 = nodes[lo];
    const c1 = nodes[lo + 1];
    const u = (t - t0) / (t1 - t0 || 1);
    for (let c = 0; c < 4; c++) {
      lut[i * 4 + c] = Math.round(c0[c] * (1 - u) + c1[c] * u);
    }
  }
  return lut;
}

function getLut(gamma, baseColor) {
  const key = `${gamma.toFixed(3)}|${(baseColor || DEFAULT_PALETTE_KEY).toLowerCase()}`;
  let lut = _lutCache.get(key);
  if (!lut) {
    lut = buildLut(gamma, baseColor);
    _lutCache.set(key, lut);
  }
  return lut;
}

// Exported so the activities-card palette picker can render a swatch
// preview of each palette without hard-coding the colors twice.
export const HEATMAP_PALETTE_KEYS = Object.keys(HEATMAP_PALETTES);
export const heatmapPaletteSwatch = (key) => {
  // Use the 0.50 stop as the swatch color — that's the perceptually
  // "in the middle" color of the gradient.
  const nodes = HEATMAP_PALETTES[key] || HEATMAP_PALETTES[DEFAULT_PALETTE_KEY];
  const [r, g, b] = nodes[3];
  return `rgb(${r}, ${g}, ${b})`;
};


// Bresenham line rasterizer — adds +1 to `accum` (Float32Array of W·H)
// for each integer pixel between (x0,y0) and (x1,y1). When `skipEnd`
// is true, the (x1,y1) pixel is NOT added — used to avoid
// double-counting vertices when chaining segments.
function rasterizeLine(accum, W, H, x0, y0, x1, y1, skipEnd) {
  x0 = Math.round(x0);
  y0 = Math.round(y0);
  x1 = Math.round(x1);
  y1 = Math.round(y1);
  const dx = Math.abs(x1 - x0);
  const dy = Math.abs(y1 - y0);
  const sx = x0 < x1 ? 1 : -1;
  const sy = y0 < y1 ? 1 : -1;
  let err = dx - dy;
  let x = x0;
  let y = y0;
  while (true) {
    const atEnd = x === x1 && y === y1;
    if (!(atEnd && skipEnd)) {
      if (x >= 0 && x < W && y >= 0 && y < H) {
        accum[y * W + x] += 1.0;
      }
    }
    if (atEnd) break;
    const e2 = err + err;
    if (e2 > -dy) { err -= dy; x += sx; }
    if (e2 < dx) { err += dx; y += sy; }
  }
}


export default function HeatLayer({
  // [{ id, coordinates: [[lat, lon], ...] }, ...] — undensified.
  polylines = [],
  // How many overlapping polylines to consider "fully saturated"
  // (top of the gradient → cream). Bump up if your hottest streets
  // look saturated too soon; drop if rare routes look too faint
  // before the gamma curve.
  saturationPasses = 30,
  // Gamma applied to normalized intensity before the LUT lookup.
  // <1 brightens the low end. 1.0 disables (linear).
  gamma = 0.35,
  // Heatmap palette key — one of HEATMAP_PALETTE_KEYS (orange, red,
  // blue, green, purple). Unrecognized values fall back to orange.
  baseColor = 'orange',
  // Overall layer opacity (0..1). Updated cheaply via CSS, no redraw.
  opacity = 1,
}) {
  const map = useMap();
  const divRef = useRef(null);
  const canvasRef = useRef(null);
  const accumBufRef = useRef(null);

  // One-time DOM setup: create the canvas overlay div on the map's
  // overlayPane. The pane translates with the map, so we reposition
  // the div on every redraw to keep its [0,0] aligned with the map
  // container's [0,0].
  useEffect(() => {
    if (!map) return undefined;

    const div = L.DomUtil.create('div', 'leaflet-heat-canvas');
    div.style.position = 'absolute';
    div.style.left = '0';
    div.style.top = '0';
    div.style.pointerEvents = 'none';
    div.style.opacity = String(opacity);
    div.style.willChange = 'transform';

    const canvas = document.createElement('canvas');
    canvas.style.display = 'block';
    div.appendChild(canvas);
    map.getPanes().overlayPane.appendChild(div);

    divRef.current = div;
    canvasRef.current = canvas;

    return () => {
      if (div.parentNode) div.parentNode.removeChild(div);
      divRef.current = null;
      canvasRef.current = null;
      accumBufRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map]);

  // Redraw on data/option/view changes.
  useEffect(() => {
    if (!map) return undefined;
    const canvas = canvasRef.current;
    const div = divRef.current;
    if (!canvas || !div) return undefined;

    const lut = getLut(gamma, baseColor);

    const redraw = () => {
      const size = map.getSize();
      const W = size.x;
      const H = size.y;
      if (W === 0 || H === 0) return;

      if (canvas.width !== W || canvas.height !== H) {
        canvas.width = W;
        canvas.height = H;
        canvas.style.width = `${W}px`;
        canvas.style.height = `${H}px`;
      }

      // Reuse the accumulator across redraws when the size matches.
      // For 1920×1080 this is ~8 MB; alloc/free per frame would be
      // pointless GC pressure.
      let accum = accumBufRef.current;
      if (!accum || accum.length !== W * H) {
        accum = new Float32Array(W * H);
        accumBufRef.current = accum;
      } else {
        accum.fill(0);
      }

      // Position the overlay so canvas [0,0] = map container [0,0].
      const topLeft = map.containerPointToLayerPoint([0, 0]);
      L.DomUtil.setPosition(div, topLeft);

      // Viewport bbox for cheap polyline cull.
      const bounds = map.getBounds();
      const south = bounds.getSouth();
      const north = bounds.getNorth();
      const west = bounds.getWest();
      const east = bounds.getEast();

      // Rasterize every polyline into the float accumulator.
      for (const poly of polylines) {
        const cs = poly && poly.coordinates;
        if (!cs || cs.length < 2) continue;

        // Polyline bbox
        let minLat = cs[0][0], maxLat = cs[0][0];
        let minLon = cs[0][1], maxLon = cs[0][1];
        for (let i = 1; i < cs.length; i++) {
          const lat = cs[i][0], lon = cs[i][1];
          if (lat < minLat) minLat = lat;
          else if (lat > maxLat) maxLat = lat;
          if (lon < minLon) minLon = lon;
          else if (lon > maxLon) maxLon = lon;
        }
        if (maxLat < south || minLat > north || maxLon < west || minLon > east) continue;

        // Project once, then rasterize segments.
        const N = cs.length;
        let prevPt = map.latLngToContainerPoint(cs[0]);
        for (let i = 1; i < N; i++) {
          const p = map.latLngToContainerPoint(cs[i]);
          // Skip the segment endpoint for all but the polyline's final
          // segment — otherwise the shared vertex pixel between
          // consecutive segments gets +2 from one polyline.
          rasterizeLine(accum, W, H, prevPt.x, prevPt.y, p.x, p.y, i < N - 1);
          prevPt = p;
        }
      }

      // Colormap pass: float accum → ImageData via gamma+orange LUT.
      const ctx = canvas.getContext('2d');
      const img = ctx.createImageData(W, H);
      const data = img.data;
      const invSat = 1.0 / Math.max(1, saturationPasses);
      const len = accum.length;
      for (let i = 0; i < len; i++) {
        const v = accum[i];
        if (v === 0) continue;
        // Normalize to [0, 1] and look up. The LUT bakes gamma in, so
        // this is a single 256-entry lookup with no pow() here.
        let t = v * invSat;
        if (t > 1) t = 1;
        const lutIdx = (t * (LUT_SIZE - 1) + 0.5) | 0;
        const pi = i * 4;
        const li = lutIdx * 4;
        data[pi] = lut[li];
        data[pi + 1] = lut[li + 1];
        data[pi + 2] = lut[li + 2];
        data[pi + 3] = lut[li + 3];
      }
      ctx.putImageData(img, 0, 0);
    };

    redraw();

    // Redraw on gesture-end events (not on every move/zoom frame); the
    // overlay visibly pans/zooms with the map during the gesture, then
    // snaps to a sharp re-render at the end.
    map.on('moveend zoomend viewreset resize', redraw);
    return () => {
      map.off('moveend zoomend viewreset resize', redraw);
    };
  }, [map, polylines, saturationPasses, gamma, baseColor]);

  // Cheap CSS opacity — no redraw.
  useEffect(() => {
    if (divRef.current) divRef.current.style.opacity = String(opacity);
  }, [opacity]);

  return null;
}
